import { AsyncResult, type Atom, AtomRegistry } from 'effect/reactivity';
import { Effect, Option } from 'effect';

/**
 * `atom`'s value, fetching it if needed. A cached value resolves at once
 * (stale ones refetch in the background); a cached failure is fetched again,
 * so retrying does not replay the old error.
 */
export const resolveAtom = <A, E>(
  registry: AtomRegistry.AtomRegistry,
  atom: Atom.Atom<AsyncResult.AsyncResult<A, E>>,
) =>
  Effect.suspend(() => {
    const before = registry.get(atom);
    if (!AsyncResult.isFailure(before))
      return AtomRegistry.getResult(registry, atom);
    // The refetch reaches `atom` after this returns, so the next settled
    // result is the answer, not the failure still showing.
    return Effect.callback<A, E>((resume) => {
      const unsubscribe = registry.subscribe(atom, (result) => {
        if (result === before || result.waiting) return;
        if (AsyncResult.isSuccess(result)) {
          unsubscribe();
          resume(Effect.succeed(result.value));
        } else if (AsyncResult.isFailure(result)) {
          unsubscribe();
          resume(Effect.failCause(result.cause));
        }
      });
      registry.refresh(atom);
      return Effect.sync(unsubscribe);
    });
  });

/**
 * Warms `atom` without failing the loader: a page that can render without
 * this value shows its own loading or error state instead.
 */
export const preloadAtom = <A, E>(
  registry: AtomRegistry.AtomRegistry,
  atom: Atom.Atom<AsyncResult.AsyncResult<A, E>>,
) => Effect.runPromise(resolveAtom(registry, atom).pipe(Effect.ignore));

/**
 * A failed refresh as the success it replaced, so a page keeps showing what
 * it had. A failure with nothing before it stays a failure.
 */
export const keepLatest = <A, E>(result: AsyncResult.AsyncResult<A, E>) =>
  AsyncResult.isFailure(result)
    ? Option.getOrElse(result.previousSuccess, () => result)
    : result;
